/**
 * Read hooks of the CAD1 screens that the data layer does not cover (PLAN cad1-phase3 §6 S2, S5, S6, S7). The logic is logic/launches.ts
 * (pure, unit-tested); these wire it to the app's public client for chain 56 and SWR. Nothing is read without a configured launcher, so a
 * production build (no launcher) makes no CAD1 request at all, on any page. Every read here covers both launchers (Bez 2026-10-09: v2 for
 * new launches, v1 read-only with its tokens still valid): config.registries, oldest first. None of them reads logs: the public BSC RPC
 * refuses eth_getLogs ranges (Bez's live test 2026-10-09), so the registries' views, multicall and the lock index are the sources.
 *
 *   useCad1Launches   both registries as one list, newest first, `pages` × 24 rows, each with its pool price and its launch lock
 *   useActivePools    the Active 24h tab: which of the loaded CAD1 pools moved since 24 h ago (read only while that tab is open)
 *   useLaunchNumber   "Launch #n" for a token, from the registries as they are now
 *   useCad1Band       the token page band: registry, launch lock, creator holdings, the CAD1 pool and the BNB it holds; a loading and an
 *                     error state of their own (review L8). Only a …cad1 address is read: the launcher refuses any other (ADDRESS_SIGNATURE).
 */
import useSWR from 'swr'
import type { Address } from 'viem'
import { usePublicClient } from 'wagmi'
import { cad1LauncherAbi } from './abi/cad1Launcher'
import { cad1PoolAbi } from './abi/reads'
import { cad1TokenAbi } from './abi/cad1Token'
import { CAD1_CHAIN_ID, CAD1_PLANNED, CAD1_WIRING } from './config'
import { useCad1Config } from './hooks'
import { addLockFacts, readActivePools, readCombinedLaunchPage, readLockFacts, readRegistryNumber, type Cad1LockFacts, type FetchJson, type LaunchRow } from './logic/launches'

const NO_REFRESH = { revalidateOnFocus: false, revalidateOnReconnect: false } as const

/** Same-origin JSON (the lock index); a non-2xx answer is a failure. */
export const fetchJson: FetchJson = (url) => fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))

export type Cad1LaunchesState = {
  rows: LaunchRow[]
  count: bigint | null
  loading: boolean
  failed: boolean
  /** every row's launch lock was found in the lock index */
  detailsComplete: boolean
  hasMore: boolean
  refresh: () => void
}

export function useCad1Launches({ pages = 1, size }: { pages?: number; size?: number } = {}): Cad1LaunchesState {
  const { registries, ready } = useCad1Config()
  const pc = usePublicClient({ chainId: CAD1_CHAIN_ID }) as any
  const key = registries.map((r) => r.address).join(',')
  const { data, error, isLoading, mutate } = useSWR(
    ready && registries.length ? ['cad1-launches', key, pages, size ?? 24] : null,
    async () => {
      let count: bigint = 0n
      const rows: LaunchRow[] = []
      for (let p = 0; p < pages; p++) {
        // eslint-disable-next-line no-await-in-loop
        const page = await readCombinedLaunchPage(pc, registries, p, size)
        ;({ count } = page)
        rows.push(...page.rows)
        if (!page.rows.length) break
      }
      // the launch lock of each row from the lock index; a row it does not have keeps its registry facts ("Details not read yet")
      const full = await addLockFacts(pc, fetchJson, rows)
      return { count, complete: full.every((r) => r.lock), rows: full }
    },
    { ...NO_REFRESH, keepPreviousData: true, refreshInterval: 60_000 },
  )
  const count = data?.count ?? null
  return {
    rows: data?.rows ?? [],
    count,
    loading: registries.length > 0 && (!ready || isLoading),
    failed: Boolean(error),
    detailsComplete: data?.complete ?? false,
    hasMore: count !== null && BigInt(data?.rows.length ?? 0) < count,
    refresh: () => {
      mutate()
    },
  }
}

export function useActivePools(pools: readonly Address[], enabled: boolean) {
  const pc = usePublicClient({ chainId: CAD1_CHAIN_ID }) as any
  const key = pools.map((p) => p.toLowerCase()).sort().join(',')
  const { data, error, isLoading, mutate } = useSWR(enabled && pools.length ? ['cad1-active', key] : null, () => readActivePools(pc, pools, BigInt(Math.floor(Date.now() / 1000) - 86_400)), {
    ...NO_REFRESH,
    refreshInterval: 120_000,
  })
  return {
    active: data,
    loading: enabled && pools.length > 0 && isLoading,
    failed: Boolean(error),
    /** reads the Active 24h tab again (its Retry; the list's own refresh does not touch this key) */
    refresh: () => {
      mutate()
    },
  }
}

/** "Launch #n" across both launchers, from the registries as they are now; `launcher` is the one that registered the token (the v2 launcher
 * when it is not known). */
export function useLaunchNumber(token: Address | undefined, launcher?: Address): bigint | null {
  const { launcher: current, registries, ready } = useCad1Config()
  const pc = usePublicClient({ chainId: CAD1_CHAIN_ID }) as any
  const own = launcher ?? current
  const { data } = useSWR(
    ready && own && token ? ['cad1-number', registries.map((r) => r.address).join(','), own, token.toLowerCase()] : null,
    () => readRegistryNumber(pc, registries, own as Address, token as Address),
    NO_REFRESH,
  )
  return data ?? null
}

export type Cad1BandDetails = {
  /** the launch lock from the lock index (creator = its owner, lock id, launch time); undefined when the index does not have it */
  lock?: Cad1LockFacts
  number: bigint | null
  sqrtPriceX96?: bigint
  feeProtocol?: number
  /** WBNB.balanceOf(pool): the BNB really in the CAD1 pool (review H1) */
  poolBnb?: bigint
  /** the creator's balance now, in bps of the supply */
  holdsBps?: bigint
  /** the token's symbol() */
  symbol?: string
}

export type Cad1BandState =
  | { kind: 'none' }
  /** the registry check, or (for a CAD1 token) its details, are being read: a skeleton of the band's size holds the place */
  | { kind: 'loading' }
  /** the registry could not be read: a line with Retry, never silence (a …cad1 look-alike would lose its warning) */
  | { kind: 'error'; retry: () => void }
  | { kind: 'fake' }
  | ({ kind: 'cad1'; token: Address; pool?: Address; failed?: boolean; retry?: () => void } & Cad1BandDetails)

/**
 * The token page band. Without a launcher nothing is read and nothing is shown. The launchers deploy tokens only at …cad1 addresses (their
 * ADDRESS_SIGNATURE check), so any other address is not a CAD1 token and is not read either. For a …cad1 address: first every registry
 * (isCad1Token, poolOf on v1 and v2), which alone decides "fake"; then, for a CAD1 token, its launch lock from the lock index, the pool
 * reads and the creator's holdings, and its number from the launcher that registered it. No logs (the public BSC RPC refuses them).
 */
export function useCad1Band(address: string | undefined): Cad1BandState {
  const { registries, ready } = useCad1Config()
  const pc = usePublicClient({ chainId: CAD1_CHAIN_ID }) as any
  const on = Boolean(ready && registries.length && typeof address === 'string' && /^0x[0-9a-fA-F]{36}cad1$/i.test(address))
  const token = (address ?? '') as Address
  const key = registries.map((r) => r.address).join(',')
  const reg = useSWR(on ? ['cad1-band-registry', key, token.toLowerCase()] : null, async () => {
    const answers = (await Promise.all(
      registries.map((r) =>
        Promise.all([
          pc.readContract({ address: r.address, abi: cad1LauncherAbi, functionName: 'isCad1Token', args: [token] }),
          pc.readContract({ address: r.address, abi: cad1LauncherAbi, functionName: 'poolOf', args: [token] }),
        ]),
      ),
    )) as [boolean, Address][]
    const at = answers.findIndex(([isCad1]) => isCad1)
    return at === -1 ? { isCad1: false, pool: answers[answers.length - 1]?.[1] } : { isCad1: true, pool: answers[at][1], registry: registries[at] }
  }, NO_REFRESH)
  const pool = reg.data?.pool
  const owner = reg.data && 'registry' in reg.data ? reg.data.registry : undefined
  const det = useSWR(on && reg.data?.isCad1 && owner ? ['cad1-band', owner.address, token.toLowerCase()] : null, async (): Promise<Cad1BandDetails> => {
    // the lock index and the number are extras: either failing leaves the band with the pool facts, never a broken state
    const [lock, number] = await Promise.all([
      pool ? readLockFacts(fetchJson, token, pool).catch(() => null) : Promise.resolve(null),
      readRegistryNumber(pc, registries, owner.address, token).catch(() => null),
    ])
    const creator = lock?.creator
    const reads = await pc.multicall({
      allowFailure: true,
      contracts: [
        { address: pool, abi: cad1PoolAbi, functionName: 'slot0' },
        { address: CAD1_WIRING.wbnb, abi: cad1TokenAbi, functionName: 'balanceOf', args: [pool] },
        { address: token, abi: cad1TokenAbi, functionName: 'symbol' },
        ...(creator ? [{ address: token, abi: cad1TokenAbi, functionName: 'balanceOf', args: [creator] }] : []),
      ],
    })
    const slot = reads[0]?.status === 'success' ? (reads[0].result as readonly unknown[]) : undefined
    const poolBnb = reads[1]?.status === 'success' ? (reads[1].result as bigint) : undefined
    const symbol = reads[2]?.status === 'success' ? (reads[2].result as string) : undefined
    const held = creator && reads[3]?.status === 'success' ? (reads[3].result as bigint) : undefined
    return {
      ...(lock ? { lock } : {}),
      number,
      sqrtPriceX96: slot ? (slot[0] as bigint) : undefined,
      feeProtocol: slot ? Number(slot[5]) : undefined,
      poolBnb,
      holdsBps: held === undefined ? undefined : (held * CAD1_PLANNED.bps) / CAD1_PLANNED.totalSupply,
      symbol,
    }
  }, NO_REFRESH)
  if (!on) return { kind: 'none' }
  if (reg.error && !reg.data) return { kind: 'error', retry: () => reg.mutate() }
  if (!reg.data) return { kind: 'loading' }
  if (!reg.data.isCad1) return { kind: 'fake' }
  if (det.error && !det.data) return { kind: 'cad1', token, pool, number: null, failed: true, retry: () => det.mutate() }
  if (!det.data) return { kind: 'loading' }
  return { kind: 'cad1', token, pool, ...det.data }
}
